<template>
  <div v-if="noAuthority" class="sale-chart-layer">無權限</div>
  <div class="sale-chart" :class="noAuthority ? 'unclickable' : ''">
    <div class="sale-chart-header">
      <div class="sale-chart-title block-title">營業狀況</div>
      <div class="sale-chart-report-link block-link"></div>
    </div>
    <div class="sale-chart-body">
      <div class="sale-left-source">
        <div class="sale-stores-list">
          <div class="sale-stores-compare">
            <div class="sale-stores-compare-title">門市比較</div>
            <div class="sale-stores-compare-icon"></div>
          </div>
          <div v-if="chartData" class="sale-stores-group">
            <div
              v-for="(line, index) in chartData.Lines"
              :key="line.Id"
              class="sale-store"
            >
              <div class="sale-store-title">
                <div
                  class="sale-store-checkbox"
                  :class="{ 'not-selected': notShowStores.includes(line.Id) }"
                  @click="clickStoreCheckbox($event, line.Id)"
                >
                  ✓
                </div>
                <div class="sale-store-name">{{ line.Name }}</div>
              </div>
              <div class="sale-store-data">
                <div class="sale-store-point">
                  <div
                    class="sale-store-point-bar"
                    :style="{
                      'background-color': getLineColorByIndex(index),
                      left:
                        totalSales === 0
                          ? '100%'
                          : `-${
                              ((totalSales - getLineTotal(line)) / totalSales) *
                              100
                            }%`,
                    }"
                  ></div>
                </div>
                <div class="sale-store-price">
                  {{ $currencyFilter(getLineTotal(line)) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="sale-right-chart">
        <div class="sale-right-chart-header">
          <div class="sale-right-chart-title">銷售額</div>
          <div class="sale-right-chart-date-range">
            <select id="chartDateRange" v-model="dateRangeType">
              <option
                v-for="item in dateRangeTypes"
                :key="item.value"
                :value="item.value"
              >
                {{ item.text }}
              </option>
            </select>
          </div>
        </div>
        <div v-if="chartData" class="sale-data-group">
          <div class="sale-data">
            <div class="sale-data-title">{{ chartData.Title.Value1.Name }}</div>
            <div class="sale-data-price">
              {{
                $currencyFilter(saleSummary.value1)
                  ? $currencyFilter(saleSummary.value1)
                  : saleSummary.value1
              }}
            </div>
          </div>
          <div class="sale-data">
            <div class="sale-data-title">{{ chartData.Title.Value2.Name }}</div>
            <div class="sale-data-price">
              {{
                $currencyFilter(saleSummary.value2)
                  ? $currencyFilter(saleSummary.value2)
                  : saleSummary.value2
              }}
            </div>
          </div>
          <div class="sale-data">
            <div class="sale-data-title">{{ chartData.Title.Value3.Name }}</div>
            <div class="sale-data-price">
              {{
                $currencyFilter(saleSummary.value3)
                  ? $currencyFilter(saleSummary.value3)
                  : saleSummary.value3
              }}
            </div>
          </div>
          <div class="sale-data">
            <div class="sale-data-title">{{ chartData.Title.Value4.Name }}</div>
            <div class="sale-data-price">
              {{ $discountFilter(saleSummary.value4) }}
            </div>
          </div>
        </div>
        <div class="chart-container">
          <canvas
            class="sale-chart-canvas"
            ref="lineChart"
            :width="chartWidth"
            :height="chartHeight"
            @click="handleClick"
          ></canvas>
        </div>
      </div>
    </div>
  </div>
  <Teleport to="body">
    <div
      v-if="tooltip.visible"
      ref="chartTooltip"
      class="sale-chart-tooltip"
      :style="{
        top: `${tooltip.top}px`,
        left: `${tooltip.left}px`,
        maxHeight: `${tooltip.maxHeight}px`,
        '--tooltip-columns': tooltip.columns,
        '--tooltip-rows': tooltip.rows,
      }"
    >
      <div class="sale-chart-tooltip-title">{{ tooltip.title }}</div>
      <div class="sale-chart-tooltip-list">
        <div
          v-for="point in tooltip.points"
          :key="point.id"
          class="sale-chart-tooltip-item"
        >
          <span
            class="sale-chart-tooltip-color"
            :style="{ backgroundColor: point.color }"
          ></span>
          <span class="sale-chart-tooltip-text">
            {{ point.name }}: {{ $currencyFilter(point.value) }}
          </span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
export default {
  data() {
    return {
      chartData: null,
      lineColors: this.$enum.chartColorType.getAll(),
      chartWidth: 900,
      chartHeight: 450,
      padding: 30,
      yTicksCount: 5,
      clickedIndex: null,
      hoveringIndex: null,
      tooltip: {
        visible: false,
        title: "",
        points: [],
        top: 0,
        left: 0,
        maxHeight: 0,
        columns: 1,
        rows: 1,
      },
      notShowStores: [],
      dateRangeTypes: this.$enum.dateRangeType.getAll(),
      dateRangeType: null,
      resizeObserver: null,
      leftPadding: null,
      rightPadding: null,
      topPadding: null,
      bottomPadding: null,
      checkTimer: null,
      authorities: null,
      noAuthority: false,
    };
  },
  computed: {
    visibleLines() {
      if (!this.chartData) return [];
      return this.chartData.Lines.filter((line) => {
        return !this.notShowStores.includes(line.Id);
      });
    },
    saleSummary() {
      const days = this.toNumber(this.chartData?.Title?.Days);
      const total = this.visibleLines.reduce((sum, line) => {
        return sum + this.getLineTotal(line);
      }, 0);
      const totalCost = this.visibleLines.reduce((sum, line) => {
        return sum + this.toNumber(line.TotalCost);
      }, 0);
      const grossProfit = total - totalCost;

      return {
        value1: this.roundToMaxThree(total),
        value2: this.roundToMaxThree(days === 0 ? 0 : total / days),
        value3: this.roundToMaxThree(grossProfit),
        value4: this.roundToMaxThree(
          total === 0 ? 0 : (grossProfit / total) * 100
        ),
      };
    },
    totalSales() {
      return this.saleSummary.value1;
    },
  },
  watch: {
    dateRangeType(newValue, oldValue) {
      if (!oldValue && oldValue !== 0) return;
      this.clickedIndex = null;
      this.hideTooltip();
      this.loadChartData(newValue).then(() => {
        this.$nextTick(() => {
          this.leftPadding = 80;
          this.rightPadding = 20;
          this.bottomPadding = 40;
          this.padding = 10; // 頂部預留
          this.initChart(true);
        });
      });
    },
  },
  methods: {
    isShow() {
      const authority = this.authorities.find(function (data) {
        return data.AuthItemType == 90;
      });
      if (authority) {
        if (authority.IsList) return true;
      }
      return false;
    },
    getLineColorByIndex(index) {
      return (
        this.lineColors[index % this.lineColors.length] ||
        "#" + this.generateColor(index)
      );
    },
    generateColor(seed) {
      // 為每個 ID 生成一個穩定的顏色
      let hash = 0;
      for (let i = 0; i < seed.toString().length; i++) {
        hash = seed.toString().charCodeAt(i) + ((hash << 5) - hash);
      }
      let color = "";
      for (let i = 0; i < 3; i++) {
        let value = (hash >> (i * 8)) & 0xff;
        color += ("00" + value.toString(16)).substr(-2);
      }
      return color;
    },
    toNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },
    getLineTotal(line) {
      return this.toNumber(line.Total ?? line.Value1);
    },
    roundToMaxThree(value) {
      return Math.round((this.toNumber(value) + Number.EPSILON) * 1000) / 1000;
    },
    clickStoreCheckbox(e, id) {
      let exist = this.notShowStores.find((storeId) => {
        return storeId === id;
      });
      if (!exist && exist !== null) {
        this.notShowStores.push(id);
      } else {
        let idIndex = this.notShowStores.findIndex((storeId) => {
          return storeId === id;
        });
        this.notShowStores.splice(idIndex, 1);
      }
      sessionStorage.setItem("chartStores", JSON.stringify(this.notShowStores));
      this.initChart();
    },
    initChart(isNewChart) {
      if (sessionStorage.getItem("chartStores"))
        this.notShowStores = JSON.parse(sessionStorage.getItem("chartStores"));

      const canvas = this.$refs.lineChart;
      if (!canvas) return;

      const container = canvas.parentNode;

      const displayWidth = container.clientWidth;
      const displayHeight = container.clientHeight;

      canvas.width = displayWidth;
      canvas.height = displayHeight;

      this.chartWidth = displayWidth;
      this.chartHeight = displayHeight;
      this.ctx = canvas.getContext("2d");

      this.ctx.scale(1, 1);

      this.calculateSteps();
      this.drawAxes();
      this.drawXLabels();
      this.drawYLabels();
      this.drawLines();

      // 如果有固定點，重新繪製
      if (!isNewChart && this.clickedIndex !== null) {
        this.showClickedPoint();
      }
    },
    calculateSteps() {
      const ctx = this.ctx;
      const yMin = this.chartData.Y.Min;
      const yMax = this.chartData.Y.Max;
      const yTicksCount = this.yTicksCount;
      const yUnit = this.chartData.Y.Unit || "";

      const tickStep = (yMax - yMin) / yTicksCount;

      // 預估 Y 軸最大標籤文字
      let maxYLabel = "";
      for (let i = 0; i <= yTicksCount; i++) {
        const yValue = yMin + i * tickStep;
        const label = `${yValue.toFixed(0)}${yUnit}`;
        if (label.length > maxYLabel.length) maxYLabel = label;
      }

      ctx.font = "14px Arial";
      const labelWidth = ctx.measureText(maxYLabel).width;

      const labelPadding = 10;
      const leftPadding = labelWidth + labelPadding + 5; // 加些 buffer
      const rightPadding = 30;
      const topPadding = 30;
      const bottomPadding = 30;

      const maxDataLength = this.chartData.XPoints.length;

      this.xStep =
        (this.chartWidth - leftPadding - rightPadding) / (maxDataLength - 1);
      this.yStep =
        (this.chartHeight - topPadding - bottomPadding) / (yMax - yMin || 1);

      this.leftPadding = leftPadding;
      this.rightPadding = rightPadding;
      this.topPadding = topPadding;
      this.bottomPadding = bottomPadding;
    },
    drawAxes() {
      const { ctx, chartWidth, chartHeight, leftPadding, bottomPadding } = this;
      // 繪製 X 軸
      ctx.beginPath();
      ctx.moveTo(leftPadding, chartHeight - bottomPadding);
      ctx.lineTo(chartWidth - this.rightPadding, chartHeight - bottomPadding);
      ctx.strokeStyle = "#727272";
      ctx.stroke();
    },
    drawXLabels() {
      const { ctx, chartHeight, leftPadding, bottomPadding } = this;
      let labels = [];
      if (this.chartData.XDispalys) {
        labels = this.chartData.XDispalys;
      } else {
        labels = this.chartData.XDisplays;
      }
      if (labels.length === 0) return;

      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillStyle = "#727272";
      ctx.font = "14px Arial";

      labels.forEach((label) => {
        const labelDate = new Date(label).getTime();
        let nearestPointIndex = 0;

        for (let i = 0; i < this.chartData.XPoints.length; i++) {
          const pointDate = new Date(this.chartData.XPoints[i].Text).getTime();
          if (
            Math.abs(pointDate - labelDate) <
            Math.abs(
              new Date(
                this.chartData.XPoints[nearestPointIndex].Text
              ).getTime() - labelDate
            )
          ) {
            nearestPointIndex = i;
          }
        }

        const x = leftPadding + nearestPointIndex * this.xStep;
        const y = chartHeight - bottomPadding + 10;

        ctx.fillText(this.formatDate(label), x, y);
      });
    },
    formatDate(dateString) {
      // 根據 TimeGranularityType 格式化日期
      if (this.chartData.TimeGranularityType > 5) {
        const isoString = dateString.replace(" ", "T");
        // 2. 建立 Date 物件
        const date = new Date(isoString);
        // 3. 取得小時與分鐘，並補零
        const hours = String(date.getHours());
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${hours}:${minutes}`;
      } else {
        const date = new Date(dateString);
        return `${date.getMonth() + 1}/${date.getDate()}`;
      }
    },
    drawYLabels() {
      const { ctx, chartWidth, chartHeight, yTicksCount } = this;

      const yMin = this.chartData.Y.Min;
      const yMax = this.chartData.Y.Max;
      const yUnit = this.chartData.Y.Unit || "";

      const tickStep = (yMax - yMin) / yTicksCount;

      const labelPadding = 10;

      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#727272";
      ctx.font = "14px Arial";

      for (let i = 0; i <= yTicksCount; i++) {
        const yValue = yMin + i * tickStep;
        const y =
          chartHeight - this.bottomPadding - (yValue - yMin) * this.yStep;

        const labelText = `${yValue.toFixed(0)}${yUnit}`;
        ctx.fillText(labelText, this.leftPadding - labelPadding, y);

        ctx.beginPath();
        ctx.moveTo(this.leftPadding, y);
        ctx.lineTo(chartWidth - this.rightPadding, y);
        ctx.strokeStyle = "#e0e0e0";
        ctx.stroke();
      }
    },
    drawLines() {
      const { ctx, chartHeight } = this;
      const yMin = this.chartData.Y.Min;

      this.chartData.Lines.forEach((line, i) => {
        if (this.notShowStores.includes(line.Id)) return;

        const color = this.getLineColorByIndex(i);
        ctx.beginPath();

        line.Points.forEach((point, index) => {
          // 確認 point 是數值還是物件
          const yValue = point;

          const x = this.leftPadding + index * this.xStep;
          const y =
            chartHeight - this.bottomPadding - (yValue - yMin) * this.yStep;

          if (index === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });

        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    },
    drawVerticalLine(x) {
      const { ctx, chartHeight } = this;
      ctx.beginPath();
      ctx.moveTo(x, this.leftPadding);
      ctx.lineTo(x, chartHeight - this.bottomPadding);
      ctx.strokeStyle = "gray";
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.setLineDash([]);
    },
    getClosestIndex(mouseX) {
      const { xStep } = this;
      // 計算滑鼠位置對應的數據點索引
      const relativeX = mouseX - this.leftPadding;
      const index = Math.round(relativeX / xStep);

      return Math.max(0, Math.min(index, this.chartData.XPoints.length - 1));
    },
    showTooltip(index) {
      if (this.chartData.XPoints.length === 0) return;

      const canvas = this.$refs.lineChart;
      if (!canvas) return;

      const canvasRect = canvas.getBoundingClientRect();
      const scaleX = canvasRect.width / canvas.width;
      const x =
        canvasRect.left + (this.leftPadding + index * this.xStep) * scaleX;
      const date = this.chartData.XPoints[index].Text;
      const edgePadding = 12;
      const canvasTop = Math.max(edgePadding, canvasRect.top);
      const canvasBottom = Math.min(
        window.innerHeight - edgePadding,
        canvasRect.bottom
      );
      const tooltipMaxHeight = Math.max(1, canvasBottom - canvasTop);

      const points = [];
      this.chartData.Lines.forEach((line, i) => {
        if (!this.notShowStores.includes(line.Id)) {
          const value = line.Points[index];
          const color = this.getLineColorByIndex(i);
          points.push({ id: `${line.Id}-${i}`, name: line.Name, value, color });
        }
      });

      const tooltipHeaderHeight = 50;
      const tooltipItemHeight = 22;
      const rows = Math.max(
        1,
        Math.floor(
          (tooltipMaxHeight - tooltipHeaderHeight) / tooltipItemHeight
        )
      );
      const columns = Math.max(1, Math.ceil(points.length / rows));

      this.tooltip = {
        visible: true,
        title:
          this.formatDate(date) +
          " " +
          new Date(date).toLocaleTimeString("zh-TW", {
            hour: "2-digit",
            minute: "2-digit",
          }),
        points,
        top: canvasTop,
        left: x + 10,
        maxHeight: tooltipMaxHeight,
        columns,
        rows,
      };

      this.$nextTick(() => {
        const tooltip = this.$refs.chartTooltip;
        if (!tooltip) return;

        const tooltipWidth = tooltip.offsetWidth;
        const tooltipHeight = tooltip.offsetHeight;
        let left = x + 10;
        if (left + tooltipWidth > window.innerWidth - edgePadding) {
          left = x - tooltipWidth - 10;
        }

        this.tooltip.left = Math.max(
          edgePadding,
          Math.min(left, window.innerWidth - tooltipWidth - edgePadding)
        );
        this.tooltip.top = Math.max(
          canvasTop,
          Math.min(canvasTop + 10, canvasBottom - tooltipHeight)
        );
      });
    },
    hideTooltip() {
      this.tooltip.visible = false;
    },
    handleMouseMove(event) {
      // 獲取相對於畫布的滑鼠位置
      const rect = event.target.getBoundingClientRect();
      const scaleX = event.target.width / rect.width;
      const offsetX = (event.clientX - rect.left) * scaleX;

      const closestIndex = this.getClosestIndex(offsetX);
      this.hoveringIndex = closestIndex;

      // 清除畫布並重新繪製基本元素
      this.ctx.clearRect(0, 0, this.chartWidth, this.chartHeight);
      this.drawAxes();
      this.drawXLabels();
      this.drawYLabels();
      this.drawLines();

      // 準確計算數據點的 x 坐標
      const x = this.leftPadding + closestIndex * this.xStep;
      this.drawVerticalLine(x);

      // 繪製每條線上的點
      this.chartData.Lines.forEach((line, i) => {
        if (
          !this.notShowStores.includes(line.Id) &&
          closestIndex < line.Points.length
        ) {
          const y =
            this.chartHeight -
            this.bottomPadding -
            (line.Points[closestIndex] - this.chartData.Y.Min) * this.yStep;

          this.ctx.beginPath();
          this.ctx.arc(x, y, 5, 0, 2 * Math.PI);
          this.ctx.fillStyle = this.getLineColorByIndex(i);
          this.ctx.fill();
        }
      });

      // 如果有固定點，重新繪製
      if (
        this.clickedIndex !== null &&
        this.clickedIndex !== this.hoveringIndex
      ) {
        this.showClickedPoint();
      }

      // 顯示門市資訊浮層
      this.showTooltip(closestIndex);
    },
    handleMouseLeave() {
      this.hoveringIndex = null;

      // 清除畫布並重新繪製基本元素
      this.ctx.clearRect(0, 0, this.chartWidth, this.chartHeight);
      this.drawAxes();
      this.drawXLabels();
      this.drawYLabels();
      this.drawLines();

      // 如果有固定點，重新繪製
      if (this.clickedIndex !== null) {
        this.showClickedPoint();
      } else {
        this.hideTooltip();
      }
    },
    handleClick(event) {
      const rect = event.target.getBoundingClientRect();
      const scaleX = event.target.width / rect.width;
      const offsetX = (event.clientX - rect.left) * scaleX;

      const closestIndex = this.getClosestIndex(offsetX);

      // 如果點擊已固定的點，取消固定
      if (this.clickedIndex === closestIndex) {
        this.clickedIndex = null;
      } else {
        // 否則固定新的點
        this.clickedIndex = closestIndex;
      }

      // 重新繪製
      this.handleMouseMove(event);
    },
    showClickedPoint() {
      if (this.clickedIndex === null) return;

      const x = this.leftPadding + this.clickedIndex * this.xStep;

      // 繪製垂直線
      this.drawVerticalLine(x);

      // 繪製每條線上的點
      this.chartData.Lines.forEach((line, i) => {
        if (
          !this.notShowStores.includes(line.Id) &&
          this.clickedIndex < line.Points.length
        ) {
          const y =
            this.chartHeight -
            this.bottomPadding -
            (line.Points[this.clickedIndex] - this.chartData.Y.Min) *
              this.yStep;

          this.ctx.beginPath();
          this.ctx.arc(x, y, 5, 0, 2 * Math.PI);
          this.ctx.fillStyle = this.getLineColorByIndex(i);
          this.ctx.fill();
        }
      });

      // 顯示固定資料點的門市資訊
      this.showTooltip(this.clickedIndex);
    },
    handleResize() {
      const canvas = this.$refs.lineChart;
      if (!canvas) return;

      const displayWidth = canvas.parentNode.clientWidth;
      const displayHeight = canvas.parentNode.clientHeight;

      canvas.width = displayWidth;
      canvas.height = displayHeight;

      this.chartWidth = displayWidth;
      this.chartHeight = displayHeight;

      this.ctx.clearRect(0, 0, this.chartWidth, this.chartHeight);

      this.calculateSteps();
      this.initChart();
    },
    loadChartData(value) {
      this.notShowStores = [];
      let type = this.dateRangeType;
      if (value || value === 0) {
        type = value;
      }
      let dTO = this.$func.getDateRange.getByType(type);
      return this.$api.charts
        .getStores(dTO)
        .then((data) => {
          this.chartData = data;
          this.chartData.Lines.forEach((line) => {
            if (line.Id.length == 0) line.Id = null;
          });
          sessionStorage.setItem("chartDateType", JSON.stringify(type));
        })
        .catch((error) => {
          this.$func.message({
            type: "error",
            mainMessage: error.data.Message,
          });
        });
    },
  },
  beforeUnmount() {
    clearInterval(this.checkTimer);
    const canvas = this.$refs.lineChart;
    if (canvas) {
      canvas.removeEventListener("mousemove", this.handleMouseMove);
      canvas.removeEventListener("mouseleave", this.handleMouseLeave);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  created() {
    this.checkTimer = setInterval(() => {
      this.authorities = JSON.parse(localStorage.getItem("authorities"));
      if (this.authorities) {
        clearInterval(this.checkTimer);
        let dTO = null;
        if (sessionStorage.getItem("chartDateType")) {
          dTO = JSON.parse(sessionStorage.getItem("chartDateType"));
          this.dateRangeType = dTO;
        } else {
          this.dateRangeType = 2;
        }
        // 加載測試資料
        if (this.isShow()) {
          this.loadChartData(dTO).then(() => {
            // 初始化圖表
            this.$nextTick(() => {
              this.leftPadding = 80;
              this.rightPadding = 20;
              this.bottomPadding = 40;
              this.padding = 10; // 頂部預留
              this.initChart();

              // 添加事件監聽器
              const canvas = this.$refs.lineChart;
              if (canvas) {
                canvas.addEventListener("mousemove", this.handleMouseMove);
                canvas.addEventListener("mouseleave", this.handleMouseLeave);

                // 設置 ResizeObserver 監聽尺寸變化
                this.resizeObserver = new ResizeObserver(() => {
                  this.handleResize();
                });
                this.resizeObserver.observe(canvas);
              }
            });
          });
        } else {
          this.noAuthority = false;
        }
      }
    }, 200); // 每200毫秒檢查一次
  },
};
</script>

<style lang="scss">
.sale-chart {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  @media screen and (max-width: 991px) {
    width: 100%;
  }
  @media screen and (max-width: 1199px) {
    padding-bottom: 44px;
  }
  &.unclickable {
    mix-blend-mode: lighten;
  }
}
.sale-chart-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.sale-chart-body {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  width: 100%;
  padding-left: 224px;
  display: flex;
  justify-content: center;
  align-items: stretch;
  border-radius: 12px;
  background-color: white;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 5px;
  @media screen and (max-width: 1199px) {
    flex-direction: column;
    height: 100%;
    padding-left: 0;
    // overflow: visible;
  }
}
.sale-chart-report-link {
  text-align: end;
  color: #4695c9;
  font-weight: 500;
}
.sale-left-source {
  position: absolute;
  inset: 0 auto 0 0;
  width: 224px;
  height: auto;
  min-height: 0;
  overflow: hidden;
  @media screen and (max-width: 1199px) {
    position: static;
    width: 100%;
    height: 100%;
    overflow: visible;
  }
}
.sale-stores-list {
  height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  // scrollbar-gutter: stable;
  @media screen and (max-width: 1199px) {
    height: auto;
    overflow-y: visible;
  }
  @media screen and (max-width: 991px) {
    overflow-x: hidden;
  }
}
.sale-stores-compare {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 20px;
  background-color: white;
  @media screen and (max-width: 1199px) {
    position: static;
  }
}
.sale-stores-compare > .sale-stores-compare-title {
  font-weight: 500;
}
.sale-stores-group {
  width: 100%;
  @media screen and (max-width: 1199px) {
    display: flex;
    justify-content: space-start;
    align-items: center;
    flex-wrap: wrap;
  }
  @media screen and (max-width: 991px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    padding-bottom: 8px;
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    scrollbar-color: #bababa #f2f2f2;
    touch-action: pan-x;

    &::-webkit-scrollbar {
      height: 6px;
    }
    &::-webkit-scrollbar-track {
      background-color: #f2f2f2;
      border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #bababa;
      border-radius: 3px;
    }
  }
}
.sale-store {
  padding: 0 20px 20px;
  @media screen and (max-width: 1199px) {
    min-width: 220px;
    width: 33%;
  }
  @media screen and (max-width: 991px) {
    flex: 0 0 200px;
    min-width: 200px;
    width: 200px;
    padding-bottom: 12px;
  }
}
.sale-store-title {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-bottom: 5px;
}
.sale-store-title > .sale-store-checkbox {
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  color: white;
  border: 1px solid #575757;
  background-color: #575757;
  border-radius: 4px;
  margin-right: 5px;
  &.not-selected {
    background-color: white !important;
  }
}
.sale-store-title > .sale-store-name {
  font-size: 16px;
  padding-bottom: 1px;
}
.sale-store-data {
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media screen and (max-width: 1199px) {
    justify-content: flex-start;
  }
}
.sale-store-data > .sale-store-point {
  position: relative;
  width: 96px;
  height: 12px;
  border-radius: 6px;
  background-color: #e4e4e4;
  overflow: hidden;
  .sale-store-point-bar {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
  }
}
.sale-store-data > .sale-store-price {
  font-size: 14px;
  color: #727272;
  margin-left: 5px;
}
.sale-right-chart {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  border-left: 1px solid #e4e4e4;
  @media screen and (max-width: 1199px) {
    width: 100%;
    border-left: none;
    border-top: 1px solid #e4e4e4;
  }
}
.sale-right-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 20px;
}
.sale-right-chart-title {
  margin: 0;
  font-weight: 500;
}
.sale-right-chart-date-range {
  #chartDateRange {
    height: 40px;
    padding: 1px 6px 0;
    border: 1px solid #bababa;
    border-radius: 4px;
    font-size: 16px;
  }
}
.sale-data-group {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  padding: 0 10px;
}
.sale-data {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  min-width: 160px;
  width: 25%;
  padding: 0 10px 10px;
}
.sale-data-title {
  font-size: 14px;
  font-weight: 400;
  color: #727272;
}
.sale-data-price {
  font-size: 20px;
  font-weight: 500;
  color: #292929;
}
.chart-container {
  width: 100%;
  max-height: 400px;
  aspect-ratio: 2 / 1;
  padding: 0 20px 10px;
}
.sale-chart-canvas {
  width: 100%;
  aspect-ratio: 2 / 1;
  max-height: 390px;
}
.sale-chart-tooltip {
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  width: max-content;
  min-width: 180px;
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 24px);
  padding: 10px;
  color: #333;
  background-color: rgba(255, 255, 255, 0.96);
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  box-shadow: rgba(0, 0, 0, 0.16) 0 2px 8px;
  pointer-events: none;
}
.sale-chart-tooltip-title {
  flex-shrink: 0;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
}
.sale-chart-tooltip-list {
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(
    var(--tooltip-columns),
    minmax(160px, max-content)
  );
  grid-template-rows: repeat(var(--tooltip-rows), 22px);
  column-gap: 12px;
  min-height: 0;
  overflow: hidden;
}
.sale-chart-tooltip-item {
  display: flex;
  align-items: center;
  gap: 5px;
  min-height: 22px;
  font-size: 14px;
  white-space: nowrap;
}
.sale-chart-tooltip-color {
  flex: 0 0 10px;
  width: 10px;
  height: 10px;
}
.sale-chart-tooltip-text {
  overflow: visible;
}
.sale-chart-layer {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  font-size: 20px;
  z-index: 12;
}
</style>
